import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import type {
  AgendaEvent,
  AgendaEventRequiredRole,
  AgendaRoster,
  ContactByTaak,
} from "@domaddress/shared";
import { formatContactName } from "../lib/contactLabel";

type GroupRow = { id: number; name: string };
type TaakRow = { id: number; name: string };

interface Props {
  event: AgendaEvent;
  onEventUpdated: (ev: AgendaEvent) => void;
}

export default function EventRosterPlanner({ event, onEventUpdated }: Props) {
  const [groups, setGroups] = useState<GroupRow[]>([]);
  const [taken, setTaken] = useState<TaakRow[]>([]);
  const [rosters, setRosters] = useState<AgendaRoster[]>([]);
  const [roles, setRoles] = useState<AgendaEventRequiredRole[]>(event.required_roles ?? []);
  const [savingRoles, setSavingRoles] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pickerRosterId, setPickerRosterId] = useState<number | null>(null);
  const [searchQ, setSearchQ] = useState("");
  const [candidates, setCandidates] = useState<ContactByTaak[]>([]);
  const [searching, setSearching] = useState(false);
  const [applyingTemplate, setApplyingTemplate] = useState(false);

  const [newRole, setNewRole] = useState({
    role_name: "",
    werkgroep_group_id: "",
    quantity: "1",
  });

  const loadRosters = useCallback(async () => {
    const rRes = await api.get<{ data: AgendaRoster[] }>(`/api/rosters?event_id=${event.id}`);
    setRosters(rRes.data.data);
  }, [event.id]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      await api.post(`/api/events/${event.id}/sync-rosters`);
      const [gRes, tRes] = await Promise.all([
        api.get<{ data: GroupRow[] }>("/api/meta/groups"),
        api.get<{ data: TaakRow[] }>("/api/meta/taken"),
      ]);
      setGroups(gRes.data.data);
      setTaken(tRes.data.data);
      await loadRosters();
    } finally {
      setLoading(false);
    }
  }, [event.id, loadRosters]);

  useEffect(() => {
    setRoles(event.required_roles ?? []);
  }, [event.required_roles]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const rostersForRole = (role: AgendaEventRequiredRole) =>
    rosters.filter(
      (r) =>
        r.role_name.toLowerCase() === role.role_name.toLowerCase() &&
        (role.werkgroep_group_id == null || r.werkgroep_group_id === role.werkgroep_group_id)
    );

  async function saveRoles(nextRoles: AgendaEventRequiredRole[]) {
    setSavingRoles(true);
    try {
      const res = await api.put<{ data: AgendaEvent }>(`/api/events/${event.id}`, {
        required_roles: nextRoles.map((r) => ({
          role_name: r.role_name,
          werkgroep_group_id: r.werkgroep_group_id,
          quantity: r.quantity,
        })),
      });
      setRoles(res.data.data.required_roles ?? []);
      onEventUpdated(res.data.data);
      await loadRosters();
    } finally {
      setSavingRoles(false);
    }
  }

  function addRequiredRole(e: React.FormEvent) {
    e.preventDefault();
    const name = newRole.role_name.trim();
    if (!name) return;
    const gid = newRole.werkgroep_group_id ? Number(newRole.werkgroep_group_id) : null;
    if (!gid) {
      alert("Kies een werkgroep — roosters zijn per werkgroep gekoppeld aan het adresboek.");
      return;
    }
    const next = [
      ...roles,
      {
        id: 0,
        event_id: event.id,
        role_name: name,
        werkgroep_group_id: gid,
        quantity: Math.max(1, Number(newRole.quantity) || 1),
      },
    ];
    void saveRoles(next);
    setNewRole({ role_name: "", werkgroep_group_id: "", quantity: "1" });
  }

  function removeRole(role: AgendaEventRequiredRole) {
    void saveRoles(roles.filter((r) => r !== role && r.id !== role.id));
  }

  async function openPicker(roster: AgendaRoster) {
    setPickerRosterId(roster.id);
    setSearchQ("");
    setSearching(true);
    try {
      const params = new URLSearchParams({
        taak: roster.role_name,
        group_id: String(roster.werkgroep_group_id),
      });
      const res = await api.get<{ data: ContactByTaak[] }>(`/api/meta/contacts-by-taak?${params}`);
      setCandidates(res.data.data);
    } finally {
      setSearching(false);
    }
  }

  async function searchCandidates() {
    const roster = rosters.find((r) => r.id === pickerRosterId);
    if (!roster) return;
    setSearching(true);
    try {
      const params = new URLSearchParams({
        taak: roster.role_name,
        group_id: String(roster.werkgroep_group_id),
      });
      if (searchQ.trim()) params.set("q", searchQ.trim());
      const res = await api.get<{ data: ContactByTaak[] }>(`/api/meta/contacts-by-taak?${params}`);
      setCandidates(res.data.data);
    } finally {
      setSearching(false);
    }
  }

  async function assignContact(contactId: number) {
    if (pickerRosterId == null) return;
    await api.post(`/api/rosters/${pickerRosterId}/assignments`, { contact_id: contactId });
    setPickerRosterId(null);
    await loadRosters();
  }

  async function clearAssignment(rosterId: number, assignmentId: number) {
    await api.delete(`/api/rosters/${rosterId}/assignments/${assignmentId}`);
    await loadRosters();
  }

  async function applyTemplate() {
    if (!event.category) return;
    if (
      roles.length > 0 &&
      !window.confirm(
        "Dit vervangt alle huidige benodigde taken door het sjabloon voor dit evenementtype. Doorgaan?"
      )
    ) {
      return;
    }
    setApplyingTemplate(true);
    try {
      const res = await api.post<{ data: AgendaEvent }>(`/api/events/${event.id}/apply-roster-template`);
      onEventUpdated(res.data.data);
      setRoles(res.data.data.required_roles ?? []);
      await loadRosters();
    } catch {
      alert(
        `Geen sjabloon voor type "${event.category}". Maak er een aan onder Rooster-sjablonen in het menu.`
      );
    } finally {
      setApplyingTemplate(false);
    }
  }

  if (loading) {
    return <p className="roster-planner-loading">Rooster laden…</p>;
  }

  const pickerRoster = rosters.find((r) => r.id === pickerRosterId);

  return (
    <div className="event-roster-planner">
      <h2>Benodigde taken &amp; rooster</h2>
      <p className="page-subtitle" style={{ marginTop: 0 }}>
        Voeg taken toe (zoals in het adresboek: Streaming-Beeld, Koffie, …). Zoek daarna iemand met
        die taak en plan die in voor dit evenement.
      </p>

      {event.category && (
        <p className="roster-template-hint">
          Evenementtype: <strong>{event.category}</strong>
          {" · "}
          <button
            type="button"
            className="btn-link"
            disabled={applyingTemplate}
            onClick={() => void applyTemplate()}
          >
            {applyingTemplate ? "Sjabloon toepassen…" : "Pas rooster-sjabloon toe"}
          </button>
          {" · "}
          <a href="/rooster-sjablonen">Sjablonen beheren</a>
        </p>
      )}

      <form className="agenda-form roster-role-form" onSubmit={addRequiredRole}>
        <div className="form-row">
          <label>Taak</label>
          <input
            list="taken-datalist"
            required
            placeholder="bijv. Streaming-Beeld"
            value={newRole.role_name}
            onChange={(e) => setNewRole({ ...newRole, role_name: e.target.value })}
          />
          <datalist id="taken-datalist">
            {taken.map((t) => (
              <option key={t.id} value={t.name} />
            ))}
          </datalist>
        </div>
        <div className="form-row">
          <label>Werkgroep</label>
          <select
            required
            value={newRole.werkgroep_group_id}
            onChange={(e) => setNewRole({ ...newRole, werkgroep_group_id: e.target.value })}
          >
            <option value="">Kies groep…</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </div>
        <div className="form-row">
          <label>Aantal personen</label>
          <input
            type="number"
            min={1}
            max={10}
            value={newRole.quantity}
            onChange={(e) => setNewRole({ ...newRole, quantity: e.target.value })}
          />
        </div>
        <button type="submit" className="btn-primary" disabled={savingRoles}>
          Taak toevoegen
        </button>
      </form>

      {roles.length === 0 && (
        <p className="roster-empty-hint">Nog geen taken voor dit evenement.</p>
      )}

      {roles.map((role) => {
        const slots = rostersForRole(role);
        const groupName = groups.find((g) => g.id === role.werkgroep_group_id)?.name;
        return (
          <div key={`${role.id}-${role.role_name}`} className="card roster-role-card">
            <div className="roster-role-header">
              <strong>{role.role_name}</strong>
              {groupName && <span className="roster-role-meta"> — {groupName}</span>}
              {role.quantity > 1 && (
                <span className="roster-role-meta"> ({role.quantity}×)</span>
              )}
              <button
                type="button"
                className="btn-text btn-danger-text"
                onClick={() => removeRole(role)}
                disabled={savingRoles}
              >
                Verwijderen
              </button>
            </div>

            {role.werkgroep_group_id == null && (
              <p className="form-error">Kies een werkgroep om te kunnen inplannen.</p>
            )}

            {slots.length === 0 && role.werkgroep_group_id != null && (
              <p className="roster-empty-hint">Geen roosterregels — sla de taak opnieuw op of herlaad.</p>
            )}

            {slots.map((slot, idx) => {
              const assigned = slot.assignments?.[0];
              return (
                <div key={slot.id} className="roster-slot">
                  {role.quantity > 1 && (
                    <span className="roster-slot-label">Plaats {idx + 1}</span>
                  )}
                  {assigned?.contact ? (
                    <div className="roster-assigned">
                      <span>
                        {formatContactName(assigned.contact)}
                        {assigned.contact.email_work && (
                          <small> · {assigned.contact.email_work}</small>
                        )}
                      </span>
                      <button
                        type="button"
                        className="btn-text"
                        onClick={() => void clearAssignment(slot.id, assigned.id)}
                      >
                        Wijzigen
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => void openPicker(slot)}
                    >
                      Persoon inplannen…
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}

      {pickerRoster && (
        <div className="roster-picker-overlay" role="presentation" onClick={() => setPickerRosterId(null)}>
          <div
            className="roster-picker card"
            role="dialog"
            aria-labelledby="roster-picker-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="popup-close"
              onClick={() => setPickerRosterId(null)}
              aria-label="Sluiten"
            >
              ×
            </button>
            <h3 id="roster-picker-title">
              Inplannen: {pickerRoster.role_name}
            </h3>
            <p className="page-subtitle">
              Personen uit het adresboek met taak &quot;{pickerRoster.role_name}&quot;
            </p>
            <div className="roster-picker-search">
              <input
                type="search"
                placeholder="Filter op naam…"
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void searchCandidates();
                  }
                }}
              />
              <button type="button" className="btn-secondary btn-sm" onClick={() => void searchCandidates()}>
                Zoeken
              </button>
            </div>
            {searching && <p>Laden…</p>}
            {!searching && candidates.length === 0 && (
              <p className="roster-empty-hint">
                Geen personen met deze taak in deze werkgroep. Voeg de taak toe aan een lid in het
                adresboek.
              </p>
            )}
            <ul className="roster-candidate-list">
              {candidates.map((c) => (
                <li key={`${c.id}-${c.group_id}`}>
                  <button type="button" className="roster-candidate-btn" onClick={() => void assignContact(c.id)}>
                    <span className="roster-candidate-name">{formatContactName(c)}</span>
                    <span className="roster-candidate-meta">{c.group_name}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
